<template>
  <div class="coaching-admin">
    <header class="manager-header">
      <div>
        <div class="eyebrow">Leadership Coaching 1:1</div>
        <h2>Sessions verwalten</h2>
        <p>
          Lege den regulären Wochenplan einmal fest. Feiertage, Präsenzschulungen und andere Abweichungen verwaltest du danach selbst als Sondertage.
        </p>
      </div>
      <v-btn icon class="refresh-action" aria-label="Daten aktualisieren" :loading="loading" @click="loadAll"><v-icon>mdi-refresh</v-icon></v-btn>
    </header>

    <nav class="manager-nav" aria-label="1:1 Verwaltungsbereiche">
      <button v-for="(item, index) in navItems" :key="item.label" type="button" :class="{ active: tab === index }" @click="selectTab(index)">
        <v-icon>{{ item.icon }}</v-icon
        ><span>{{ item.label }}</span
        ><small v-if="item.count !== null">{{ item.count }}</small>
      </button>
    </nav>

    <v-tabs-items v-model="tab" dark class="tab-content">
      <v-tab-item>
        <section class="panel">
          <div class="panel-heading">
            <div>
              <h3>Wiederkehrende Wochenfenster</h3>
              <p>Aus diesen Fenstern werden innerhalb des gewählten Gültigkeitszeitraums automatisch buchbare Zeitslots erzeugt.</p>
            </div>
            <v-btn outlined color="#58d388" class="compact-action" @click="addWindow"><v-icon left>mdi-plus</v-icon>Fenster</v-btn>
          </div>

          <div class="window-list">
            <article v-for="(windowItem, index) in schedule.weeklyWindows" :key="index" class="window-row">
              <div class="window-main-fields">
                <v-select
                  v-model="windowItem.weekday"
                  :items="weekdays"
                  item-text="label"
                  item-value="value"
                  label="Wochentag"
                  outlined
                  dense
                  hide-details />
                <v-text-field v-model="windowItem.start" type="time" step="1800" label="Von" outlined dense hide-details />
                <v-text-field v-model="windowItem.end" type="time" step="1800" label="Bis" outlined dense hide-details />
                <v-select v-model="windowItem.slotMinutes" :items="slotDurations" label="Slotdauer" suffix="Min" outlined dense hide-details />
              </div>
              <div class="window-validity">
                <div class="window-validity-heading">
                  <v-icon>mdi-calendar-range</v-icon>
                  <div>
                    <strong>Gültige Wochen</strong>
                    <small>Leer bedeutet dauerhaft</small>
                  </div>
                </div>
                <div class="window-validity-fields">
                  <v-text-field
                    v-model="windowItem.validFrom"
                    type="date"
                    label="Gültig ab"
                    :max="windowItem.validUntil || undefined"
                    clearable
                    outlined
                    dense
                    hide-details />
                  <v-text-field
                    v-model="windowItem.validUntil"
                    type="date"
                    label="Gültig bis"
                    :min="windowItem.validFrom || undefined"
                    clearable
                    outlined
                    dense
                    hide-details />
                </div>
              </div>
              <v-btn icon class="delete-action" aria-label="Fenster entfernen" @click="removeWindow(index)"
                ><v-icon>mdi-delete-outline</v-icon></v-btn
              >
            </article>
          </div>
          <div v-if="!schedule.weeklyWindows.length" class="empty-inline">Ohne Wochenfenster werden keine regulären Sessions angeboten.</div>

          <section class="booking-rules" aria-labelledby="booking-rules-title">
            <div class="booking-rules-heading">
              <span><v-icon>mdi-tune-variant</v-icon></span>
              <div>
                <h4 id="booking-rules-title">Buchungsregeln</h4>
                <p>Lege fest, wie weit im Voraus Sessions sichtbar sind und bis wann sie gebucht oder storniert werden können.</p>
              </div>
            </div>
            <div class="rules-grid">
              <article class="rule-card">
                <v-text-field
                  v-model.number="schedule.bookingHorizonWeeks"
                  type="number"
                  inputmode="numeric"
                  label="Einsehbar im Voraus"
                  suffix="Wochen"
                  min="1"
                  max="26"
                  step="1"
                  outlined
                  dense
                  :rules="horizonRules" />
                <p>So viele Wochen können Führungskräfte vorausblättern. Angezeigt werden nur Wochen innerhalb der oben gesetzten Gültigkeit.</p>
              </article>
              <article class="rule-card">
                <v-text-field
                  v-model.number="schedule.minimumLeadHours"
                  type="number"
                  inputmode="numeric"
                  label="Mindestvorlauf"
                  suffix="Stunden"
                  min="0"
                  max="336"
                  step="1"
                  outlined
                  dense
                  :rules="hoursRules" />
                <p>Eine Buchung ist nur bis zu diesem Abstand vor dem Termin möglich. 0 erlaubt sofortige Buchungen.</p>
              </article>
              <article class="rule-card">
                <v-text-field
                  v-model.number="schedule.cancellationDeadlineHours"
                  type="number"
                  inputmode="numeric"
                  label="Stornofrist"
                  suffix="Stunden"
                  min="0"
                  max="336"
                  step="1"
                  outlined
                  dense
                  :rules="hoursRules" />
                <p>Bis zu diesem Abstand dürfen Führungskräfte selbst stornieren. 0 erlaubt Stornierungen bis zum Termin.</p>
              </article>
            </div>
          </section>

          <div class="self-service-note">
            <span><v-icon>mdi-calendar-sync-outline</v-icon></span>
            <div>
              <strong>Für einzelne Abweichungen bleibt der Wochenplan unverändert.</strong>
              <p>
                Sperre Feiertage oder Präsenzschulungen unter „Sondertage“. Dort kannst du für ein bestimmtes Datum auch eigene Buchungszeiten
                freigeben.
              </p>
            </div>
            <v-btn outlined color="#58d388" class="compact-action" @click="selectTab(1)">
              <v-icon left>mdi-calendar-star-outline</v-icon>Sondertag erfassen
            </v-btn>
          </div>

          <v-alert v-if="scheduleError" type="error" outlined text>{{ scheduleError }}</v-alert>
          <div class="panel-actions">
            <v-btn color="#009640" depressed class="primary-action" :loading="savingSchedule" @click="saveSchedule">
              <v-icon left>mdi-content-save-outline</v-icon>Zeitplan und Regeln speichern
            </v-btn>
          </div>
        </section>
      </v-tab-item>

      <v-tab-item>
        <section class="panel">
          <div class="panel-heading">
            <div>
              <h3>Sondertage</h3>
              <p>Sperre einzelne Tage oder hinterlege abweichende Buchungszeiten, ohne den Wochenplan zu verändern.</p>
            </div>
            <v-btn color="#009640" depressed class="compact-action" @click="openException()"><v-icon left>mdi-plus</v-icon>Sondertag</v-btn>
          </div>
          <div v-if="exceptions.length" class="item-list">
            <article v-for="item in exceptions" :key="item._id" class="list-card exception-card">
              <span class="list-icon" :class="item.mode === 'closed' ? 'is-closed' : 'is-custom'"
                ><v-icon>{{ item.mode === "closed" ? "mdi-calendar-remove-outline" : "mdi-calendar-clock-outline" }}</v-icon></span
              >
              <div>
                <strong>{{ formatDateOnly(item.date) }}</strong>
                <p>{{ item.mode === "closed" ? "Ganzer Tag nicht buchbar" : formatWindows(item.windows) }}</p>
                <small v-if="item.reason">{{ item.reason }}</small>
                <button v-if="exceptionBookingCount(item.date)" type="button" class="booking-warning" @click="selectTab(2)">
                  <v-icon>mdi-alert-outline</v-icon>{{ exceptionBookingCount(item.date) }} bestehende
                  {{ exceptionBookingCount(item.date) === 1 ? "Buchung prüfen" : "Buchungen prüfen" }}
                </button>
              </div>
              <div class="exception-actions">
                <v-btn icon class="edit-action" :disabled="Boolean(deletingException)" aria-label="Sondertag bearbeiten" @click="openException(item)"
                  ><v-icon>mdi-pencil-outline</v-icon></v-btn
                >
                <v-btn
                  icon
                  class="delete-action"
                  :loading="deletingException === item._id"
                  :disabled="Boolean(deletingException)"
                  aria-label="Sondertag löschen"
                  @click="deleteException(item)"
                  ><v-icon>mdi-delete-outline</v-icon></v-btn
                >
              </div>
            </article>
          </div>
          <div v-else class="empty-state">
            <v-icon>mdi-calendar-check-outline</v-icon>
            <h3>Keine Sondertage</h3>
            <p>Der reguläre Wochenplan gilt ohne Abweichungen.</p>
          </div>
        </section>
      </v-tab-item>

      <v-tab-item>
        <section class="panel">
          <div class="panel-heading">
            <div>
              <h3>Gebuchte 1:1-Sessions</h3>
              <p>Reguläre Termine sind direkt bestätigt. Extra-Session-Anfragen bearbeitest du im separaten Bereich.</p>
            </div>
          </div>
          <div v-if="bookings.length" class="item-list">
            <article v-for="booking in sortedBookings" :key="booking._id" class="list-card booking-card" :class="`is-${booking.status || 'confirmed'}`">
              <span class="list-icon" :class="`icon-${booking.status || 'confirmed'}`"><v-icon>{{ adminBookingIcon(booking.status) }}</v-icon></span>
              <div class="list-copy">
                <div class="booking-person-row">
                  <strong>{{ personName(booking) }}</strong>
                  <span class="status-badge" :class="`status-${booking.status || 'confirmed'}`">{{ bookingStatus(booking.status) }}</span>
                </div>
                <p class="booking-date"><v-icon>mdi-calendar-clock-outline</v-icon>{{ formatDateTime(booking.startsAt) }}</p>
                <div class="booking-topic">
                  <span>Thema</span>
                  <p>{{ booking.topic || "Ohne Thema" }}</p>
                </div>
                <small><v-icon>mdi-map-marker-account-outline</v-icon>{{ personMeta(booking) }}</small>
                <small v-if="booking.adminNote" class="admin-note"><v-icon>mdi-message-text-outline</v-icon>{{ booking.adminNote }}</small>
              </div>
              <div class="booking-actions">
                <v-btn
                  v-if="isActiveBooking(booking) && (booking.joinUrl || booking.teamsJoinUrl)"
                  :href="booking.joinUrl || booking.teamsJoinUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  outlined
                  color="#58d388"
                  class="compact-action">
                  <v-icon left>mdi-microsoft-teams</v-icon>Teams
                </v-btn>
                <v-btn
                  v-else-if="canRetryMeeting(booking)"
                  outlined
                  color="#ff8d98"
                  class="compact-action"
                  :loading="retryingBooking === booking._id"
                  :disabled="Boolean(retryingBooking)"
                  @click="retryMeeting(booking)">
                  <v-icon left>mdi-refresh</v-icon>{{ booking.status === "meeting_failed" ? "Teams erneut erstellen" : "Teams jetzt erstellen" }}
                </v-btn>
                <span v-else-if="isActiveBooking(booking)" class="muted-badge">Link ausstehend</span>
                <v-btn
                  v-if="canCancelAdminBooking(booking)"
                  outlined
                  color="#ff8d98"
                  class="compact-action"
                  @click="askCancelBooking(booking)">
                  <v-icon left>mdi-calendar-remove-outline</v-icon>Stornieren
                </v-btn>
              </div>
            </article>
          </div>
          <div v-else class="empty-state">
            <v-icon>mdi-calendar-blank-outline</v-icon>
            <h3>Noch keine Buchungen</h3>
            <p>Regulär gebuchte und bestätigte 1:1-Sessions erscheinen hier.</p>
          </div>
        </section>
      </v-tab-item>

      <v-tab-item>
        <section class="panel">
          <div class="panel-heading">
            <div>
              <h3>Extra-Session-Anfragen</h3>
              <p>Prüfe den Fall, wähle einen Termin und sende eine Bestätigung oder Rückfrage.</p>
            </div>
          </div>
          <div v-if="extraRequests.length" class="request-grid">
            <article v-for="request in sortedRequests" :key="request._id" class="request-card">
              <div class="request-top">
                <span class="list-icon is-extra"><v-icon>mdi-calendar-star</v-icon></span>
                <div>
                  <strong>{{ personName(request) }}</strong>
                  <p>{{ personMeta(request) }}</p>
                </div>
                <span class="status-badge" :class="`status-${request.status || 'submitted'}`">{{ requestStatus(request.status) }}</span>
              </div>
              <div class="request-copy">
                <span>Fallbeschreibung</span>
                <p>{{ request.caseDescription }}</p>
                <span>Ziele</span>
                <p>{{ request.goals }}</p>
              </div>
              <div class="preferred-list">
                <span v-for="(option, index) in request.preferredOptions || []" :key="index">
                  <v-icon>mdi-calendar-clock-outline</v-icon>{{ formatDateTime(option.startsAt) }} · {{ option.durationMinutes }} Min
                </span>
              </div>
              <v-btn
                outlined
                color="#58d388"
                class="request-action"
                :disabled="isClosedRequest(request) || !scheduleLoaded"
                @click="openRequest(request)">
                <v-icon left>{{ isClosedRequest(request) ? "mdi-lock-outline" : "mdi-message-processing-outline" }}</v-icon>
                {{ isClosedRequest(request) ? "Abgeschlossen" : "Bearbeiten" }}
              </v-btn>
            </article>
          </div>
          <div v-else class="empty-state">
            <v-icon>mdi-inbox-check-outline</v-icon>
            <h3>Keine Extra-Anfragen</h3>
            <p>Neue Anfragen erscheinen automatisch hier.</p>
          </div>
        </section>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="exceptionDialog" max-width="36rem" dark persistent>
      <v-card dark class="dialog-card">
        <v-card-title class="dialog-title"
          ><span><v-icon>mdi-calendar-edit-outline</v-icon></span>
          <div>
            <small>Selbstverwaltung</small>
            <h3>{{ editingExceptionId ? "Sondertag bearbeiten" : "Sondertag erfassen" }}</h3>
          </div>
          <v-spacer /><v-btn icon :disabled="savingException" aria-label="Dialog schliessen" @click="closeException"
            ><v-icon>mdi-close</v-icon></v-btn
          ></v-card-title
        >
        <v-card-text class="dialog-body">
          <v-form ref="exceptionForm" v-model="exceptionValid">
            <div class="form-grid">
              <v-text-field
                v-model="exceptionForm.date"
                type="date"
                label="Datum"
                :min="today"
                :readonly="Boolean(editingExceptionId)"
                outlined
                :rules="requiredRules" />
              <v-select
                v-model="exceptionForm.mode"
                :items="exceptionModes"
                item-text="label"
                item-value="value"
                label="Buchbarkeit"
                outlined
                :rules="requiredRules" />
            </div>
            <div v-if="exceptionForm.mode === 'custom'" class="form-grid">
              <v-text-field v-model="exceptionForm.start" type="time" step="1800" label="Von" outlined :rules="requiredRules" />
              <v-text-field v-model="exceptionForm.end" type="time" step="1800" label="Bis" outlined :rules="requiredRules" />
              <v-select
                v-model="exceptionForm.slotMinutes"
                :items="slotDurations"
                label="Slotdauer"
                suffix="Min"
                outlined
                :rules="requiredRules"
                class="full-grid-field" />
            </div>
            <v-text-field
              v-model="exceptionForm.reason"
              label="Grund (optional)"
              placeholder="z. B. Feiertag oder Präsenzschulung"
              outlined
              counter="300" />
            <v-alert v-if="affectedExceptionBookings.length" type="warning" outlined text class="booking-alert">
              {{ affectedExceptionBookingMessage }}
              Der Sondertag entfernt nur freie Slots. Prüfe bestehende Termine anschliessend unter „Buchungen“.
              <v-btn text color="#fcd34d" class="alert-action" @click="openExceptionBookings">Buchungen öffnen</v-btn>
            </v-alert>
            <v-alert v-if="exceptionError" type="error" outlined text>{{ exceptionError }}</v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions"
          ><v-btn text color="#d4d4d8" :disabled="savingException" @click="closeException">Abbrechen</v-btn
          ><v-btn color="#009640" depressed class="primary-action" :loading="savingException" :disabled="!exceptionValid" @click="saveException"
            >Sondertag speichern</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog v-model="requestDialog" max-width="40rem" dark persistent scrollable>
      <v-card v-if="selectedRequest" dark class="dialog-card">
        <v-card-title class="dialog-title"
          ><span><v-icon>mdi-calendar-star</v-icon></span>
          <div>
            <small>Extra-Session</small>
            <h3>{{ personName(selectedRequest) }}</h3>
          </div>
          <v-spacer /><v-btn icon :disabled="savingRequest" @click="requestDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title
        >
        <v-card-text class="dialog-body">
          <v-form ref="requestForm" v-model="requestValid">
            <v-select
              v-model="requestForm.status"
              :items="requestStatuses"
              item-text="label"
              item-value="value"
              label="Entscheid"
              outlined
              :rules="requiredRules" />
            <div v-if="requestForm.status === 'confirmed'" class="request-choice-section">
              <span class="request-choice-label">Passenden Termin auswählen</span>
              <v-radio-group v-model="requestForm.optionIndex" class="request-choice-group" :rules="requiredRules">
                <v-radio
                  v-for="(option, index) in selectedRequest.preferredOptions || []"
                  :key="`${option.startsAt}-${index}`"
                  :value="index"
                  class="request-choice">
                  <template #label>
                    <span class="request-choice-copy">
                      <strong>Termin {{ index + 1 }}</strong>
                      <span>{{ formatDateTime(option.startsAt) }} · {{ option.durationMinutes }} Min</span>
                    </span>
                  </template>
                </v-radio>
              </v-radio-group>
              <p class="request-choice-help">Passt keiner der beiden Vorschläge, sende stattdessen eine Rückfrage.</p>
            </div>
            <v-textarea v-model="requestForm.adminNote" label="Nachricht an die Führungskraft" outlined rows="4" counter="1200" />
            <v-alert v-if="requestError" type="error" outlined text>{{ requestError }}</v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions"
          ><v-btn text color="#d4d4d8" :disabled="savingRequest" @click="requestDialog = false">Abbrechen</v-btn
          ><v-btn color="#009640" depressed class="primary-action" :loading="savingRequest" :disabled="!requestValid" @click="saveRequest"
            >Antwort speichern</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="30rem" dark persistent>
      <v-card v-if="cancelTarget" dark class="dialog-card confirm-card">
        <span class="warning-icon"><v-icon>mdi-calendar-remove-outline</v-icon></span>
        <h3>Session wirklich stornieren?</h3>
        <p>{{ personName(cancelTarget) }}<br />{{ formatDateTime(cancelTarget.startsAt) }}</p>
        <small>Eine verknüpfte Extra-Anfrage wird ebenfalls als storniert markiert.</small>
        <div>
          <v-btn text color="#d4d4d8" :disabled="cancellingBooking" @click="cancelDialog = false">Behalten</v-btn>
          <v-btn color="#d94b5b" depressed :loading="cancellingBooking" @click="cancelBooking">Verbindlich stornieren</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

function unwrapList(result, key) {
  if (Array.isArray(result)) return result
  return (result && Array.isArray(result[key]) && result[key]) || []
}

const zurichDateFormatter = new Intl.DateTimeFormat("de-CH", {
  timeZone: "Europe/Zurich",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

function zurichDateKey(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const parts = zurichDateFormatter.formatToParts(date).reduce((result, part) => {
    if (["year", "month", "day"].includes(part.type)) result[part.type] = part.value
    return result
  }, {})
  return parts.year && parts.month && parts.day ? `${parts.year}-${parts.month}-${parts.day}` : ""
}

function integerOr(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback
  const number = Number(value)
  return Number.isInteger(number) ? number : fallback
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/
const SLOT_DURATIONS = [60, 30]

function normalizeSlotMinutes(value, fallback = 60) {
  const minutes = Number(value)
  return SLOT_DURATIONS.includes(minutes) ? minutes : fallback
}

function isValidSlotWindow(item = {}) {
  if (!TIME_PATTERN.test(item.start || "") || !TIME_PATTERN.test(item.end || "")) return false
  const [startHours, startMinutes] = item.start.split(":").map(Number)
  const [endHours, endMinutes] = item.end.split(":").map(Number)
  const start = startHours * 60 + startMinutes
  const end = endHours * 60 + endMinutes
  const slotMinutes = Number(item.slotMinutes)
  return start % 30 === 0 && end % 30 === 0 && SLOT_DURATIONS.includes(slotMinutes) && end - start >= slotMinutes
}

function normalizeWeeklyWindow(item = {}) {
  return {
    ...item,
    slotMinutes: normalizeSlotMinutes(item.slotMinutes, 30),
    validFrom: item.validFrom || "",
    validUntil: item.validUntil || "",
  }
}

export default {
  name: "LeadershipCoachingAdmin",
  data() {
    return {
      tab: 0,
      loading: false,
      schedule: { weeklyWindows: [], bookingHorizonWeeks: null, minimumLeadHours: null, cancellationDeadlineHours: null },
      scheduleLoaded: false,
      savingSchedule: false,
      scheduleError: "",
      exceptions: [],
      bookings: [],
      extraRequests: [],
      weekdays: [
        { label: "Montag", value: 1 },
        { label: "Dienstag", value: 2 },
        { label: "Mittwoch", value: 3 },
        { label: "Donnerstag", value: 4 },
        { label: "Freitag", value: 5 },
        { label: "Samstag", value: 6 },
        { label: "Sonntag", value: 7 },
      ],
      slotDurations: SLOT_DURATIONS,
      requiredRules: [(value) => (value !== null && value !== undefined && value !== "") || "Pflichtfeld"],
      horizonRules: [
        (value) => (value !== null && value !== undefined && value !== "") || "Pflichtfeld",
        (value) => (Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 26) || "Erlaubt sind 1 bis 26 Wochen",
      ],
      hoursRules: [
        (value) => (value !== null && value !== undefined && value !== "") || "Pflichtfeld",
        (value) => (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 336) || "Erlaubt sind 0 bis 336 Stunden",
      ],
      exceptionDialog: false,
      exceptionValid: false,
      editingExceptionId: "",
      exceptionForm: { date: "", mode: "closed", start: "12:00", end: "17:30", slotMinutes: 60, reason: "" },
      exceptionModes: [
        { label: "Ganzer Tag nicht buchbar", value: "closed" },
        { label: "Abweichende Buchungszeiten", value: "custom" },
      ],
      savingException: false,
      deletingException: "",
      retryingBooking: "",
      cancelDialog: false,
      cancelTarget: null,
      cancellingBooking: false,
      exceptionError: "",
      requestDialog: false,
      requestValid: false,
      selectedRequest: null,
      requestForm: { status: "confirmed", optionIndex: 0, adminNote: "" },
      requestStatuses: [
        { label: "Termin bestätigen", value: "confirmed" },
        { label: "Rückfrage senden", value: "needs_info" },
        { label: "Anfrage ablehnen", value: "declined" },
      ],
      savingRequest: false,
      requestError: "",
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    navItems() {
      return [
        { label: "Wochenplan", icon: "mdi-calendar-week-outline", count: this.schedule.weeklyWindows.length },
        { label: "Sondertage", icon: "mdi-calendar-remove-outline", count: this.exceptions.length },
        { label: "Buchungen", icon: "mdi-account-clock-outline", count: this.bookings.length },
        {
          label: "Extra-Anfragen",
          icon: "mdi-calendar-star",
          count: this.extraRequests.filter((item) => !item.status || ["submitted", "needs_info"].includes(item.status)).length,
        },
      ]
    },
    sortedBookings() {
      const weights = { approval_pending: 0, meeting_failed: 1, meeting_pending: 2, confirmed: 3, declined: 4, cancelled: 5, canceled: 5 }
      return [...this.bookings].sort((first, second) => {
        const firstWeight = weights[first.status] === undefined ? 3 : weights[first.status]
        const secondWeight = weights[second.status] === undefined ? 3 : weights[second.status]
        const statusDifference = firstWeight - secondWeight
        return statusDifference || new Date(first.startsAt) - new Date(second.startsAt)
      })
    },
    sortedRequests() {
      return [...this.extraRequests].sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt))
    },
    today() {
      return zurichDateKey(new Date())
    },
    affectedExceptionBookings() {
      if (!this.exceptionForm.date) return []
      return this.bookings.filter((booking) => this.isActiveBooking(booking) && zurichDateKey(booking.startsAt) === this.exceptionForm.date)
    },
    affectedExceptionBookingMessage() {
      const count = this.affectedExceptionBookings.length
      return count === 1 ? "Für diesen Tag besteht bereits eine aktive Buchung." : `Für diesen Tag bestehen bereits ${count} aktive Buchungen.`
    },
  },
  mounted() {
    this.syncTab()
    this.loadAll()
  },
  watch: {
    "$route.query.section"() {
      this.syncTab()
    },
  },
  methods: {
    syncTab() {
      const names = ["schedule", "exceptions", "bookings", "requests"]
      const index = names.indexOf(this.$route.query.section)
      if (index >= 0) this.tab = index
    },
    selectTab(index) {
      this.tab = index
      const names = ["schedule", "exceptions", "bookings", "requests"]
      const query = { ...this.$route.query, tab: "coaching", section: names[index] }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadAll() {
      this.loading = true
      await Promise.all([this.loadSchedule(), this.loadExceptions(), this.loadBookings(), this.loadRequests()])
      this.loading = false
    },
    async loadSchedule() {
      try {
        const result = await api.akademie.fetchLeadershipCoachingSchedule()
        this.schedule = {
          ...this.schedule,
          ...(result || {}),
          weeklyWindows: ((result && result.weeklyWindows) || []).map(normalizeWeeklyWindow),
          bookingHorizonWeeks: integerOr(result && result.bookingHorizonWeeks, 8),
          minimumLeadHours: integerOr(result && result.minimumLeadHours, 24),
          cancellationDeadlineHours: integerOr(result && result.cancellationDeadlineHours, 24),
        }
        this.scheduleLoaded = true
      } catch (error) {
        this.schedule = { ...this.schedule, weeklyWindows: [] }
        this.scheduleLoaded = false
      }
    },
    async loadExceptions() {
      try {
        this.exceptions = unwrapList(await api.akademie.fetchLeadershipCoachingExceptions({ from: this.today }), "exceptions")
      } catch (error) {
        this.exceptions = []
      }
    },
    async loadBookings() {
      try {
        this.bookings = unwrapList(await api.akademie.fetchLeadershipCoachingBookings(), "bookings")
      } catch (error) {
        this.bookings = []
      }
    },
    async loadRequests() {
      try {
        this.extraRequests = unwrapList(await api.akademie.fetchLeadershipExtraRequests(), "requests")
      } catch (error) {
        this.extraRequests = []
      }
    },
    async retryMeeting(booking) {
      if (!this.canRetryMeeting(booking) || this.retryingBooking) return
      this.retryingBooking = booking._id
      try {
        await api.akademie.retryLeadershipCoachingMeeting(booking._id)
        this.notify("success", "Die Teams-Sitzung wurde erstellt.")
        await this.loadBookings()
      } catch (error) {
        const message = error.response && error.response.data && (error.response.data.error || error.response.data.message)
        this.notify("error", message || "Die Teams-Sitzung konnte nicht erstellt werden.")
      } finally {
        this.retryingBooking = ""
      }
    },
    askCancelBooking(booking) {
      if (!this.isActiveBooking(booking)) return
      this.cancelTarget = booking
      this.cancelDialog = true
    },
    async cancelBooking() {
      if (!this.cancelTarget) return
      this.cancellingBooking = true
      try {
        await api.akademie.cancelLeadershipCoachingBooking(this.cancelTarget._id)
        this.cancelDialog = false
        this.cancelTarget = null
        this.notify("success", "Die Session wurde storniert.")
        await Promise.all([this.loadBookings(), this.loadRequests(), this.loadExceptions()])
      } catch (error) {
        const data = error.response && error.response.data
        this.notify("error", (data && (data.error || data.message)) || "Die Session konnte nicht storniert werden.")
      } finally {
        this.cancellingBooking = false
      }
    },
    addWindow() {
      this.schedule.weeklyWindows.push({ weekday: 4, start: "12:00", end: "17:30", slotMinutes: 60, validFrom: "", validUntil: "" })
    },
    removeWindow(index) {
      this.schedule.weeklyWindows.splice(index, 1)
    },
    async saveSchedule() {
      const invalid = this.schedule.weeklyWindows.some(
        (item) => !item.weekday || !isValidSlotWindow(item),
      )
      if (invalid) {
        this.scheduleError =
          "Bitte prüfe Wochentag, Start, Ende und Slotdauer. Zeiten müssen auf :00 oder :30 liegen und das Fenster mindestens einen Slot enthalten."
        return
      }
      const invalidValidity = this.schedule.weeklyWindows.some(
        (item) =>
          (item.validFrom && !DATE_KEY_PATTERN.test(item.validFrom)) ||
          (item.validUntil && !DATE_KEY_PATTERN.test(item.validUntil)) ||
          (item.validFrom && item.validUntil && item.validFrom > item.validUntil),
      )
      if (invalidValidity) {
        this.scheduleError = "Bitte prüfe die Gültigkeit der Wochenfenster. «Gültig bis» muss am oder nach «Gültig ab» liegen."
        return
      }
      const bookingHorizonWeeks = Number(this.schedule.bookingHorizonWeeks)
      const minimumLeadHours = Number(this.schedule.minimumLeadHours)
      const cancellationDeadlineHours = Number(this.schedule.cancellationDeadlineHours)
      const invalidRules =
        !Number.isInteger(bookingHorizonWeeks) ||
        bookingHorizonWeeks < 1 ||
        bookingHorizonWeeks > 26 ||
        !Number.isInteger(minimumLeadHours) ||
        minimumLeadHours < 0 ||
        minimumLeadHours > 336 ||
        !Number.isInteger(cancellationDeadlineHours) ||
        cancellationDeadlineHours < 0 ||
        cancellationDeadlineHours > 336
      if (invalidRules) {
        this.scheduleError = "Bitte prüfe die Buchungsregeln. Erlaubt sind 1 bis 26 Wochen sowie 0 bis 336 Stunden für Vorlauf und Stornofrist."
        return
      }
      this.savingSchedule = true
      this.scheduleError = ""
      try {
        const weeklyWindows = this.schedule.weeklyWindows.map((item) => ({
          weekday: item.weekday,
          start: item.start,
          end: item.end,
          slotMinutes: Number(item.slotMinutes),
          validFrom: item.validFrom || null,
          validUntil: item.validUntil || null,
        }))
        const result = await api.akademie.updateLeadershipCoachingSchedule({
          weeklyWindows,
          bookingHorizonWeeks,
          minimumLeadHours,
          cancellationDeadlineHours,
        })
        this.schedule = {
          ...this.schedule,
          ...(result || {}),
          weeklyWindows: ((result && result.weeklyWindows) || weeklyWindows).map(normalizeWeeklyWindow),
          bookingHorizonWeeks: integerOr(result && result.bookingHorizonWeeks, bookingHorizonWeeks),
          minimumLeadHours: integerOr(result && result.minimumLeadHours, minimumLeadHours),
          cancellationDeadlineHours: integerOr(result && result.cancellationDeadlineHours, cancellationDeadlineHours),
        }
        this.notify("success", "Wochenplan und Buchungsregeln wurden gespeichert.")
      } catch (error) {
        const data = error.response && error.response.data
        this.scheduleError = (data && (data.error || data.message)) || "Zeitplan und Buchungsregeln konnten nicht gespeichert werden."
      } finally {
        this.savingSchedule = false
      }
    },
    openException(item = null) {
      const firstWindow = item && item.windows && item.windows[0]
      this.editingExceptionId = (item && item._id) || ""
      this.exceptionForm = {
        date: (item && item.date) || "",
        mode: (item && item.mode) || "closed",
        start: (firstWindow && firstWindow.start) || "12:00",
        end: (firstWindow && firstWindow.end) || "17:30",
        slotMinutes: normalizeSlotMinutes(firstWindow && firstWindow.slotMinutes, firstWindow ? 30 : 60),
        reason: (item && item.reason) || "",
      }
      this.exceptionError = ""
      this.exceptionDialog = true
      this.$nextTick(() => this.$refs.exceptionForm && this.$refs.exceptionForm.resetValidation())
    },
    closeException() {
      if (this.savingException) return
      this.exceptionDialog = false
      this.editingExceptionId = ""
      this.exceptionError = ""
    },
    openExceptionBookings() {
      this.closeException()
      this.selectTab(2)
    },
    async saveException() {
      if (!this.$refs.exceptionForm.validate()) return
      if (this.exceptionForm.mode === "custom" && !isValidSlotWindow(this.exceptionForm)) {
        this.exceptionError =
          "Bitte prüfe Start, Ende und Slotdauer. Zeiten müssen auf :00 oder :30 liegen und das Fenster mindestens einen Slot enthalten."
        return
      }
      this.savingException = true
      this.exceptionError = ""
      try {
        const wasEditing = Boolean(this.editingExceptionId)
        const affectedBookings = this.affectedExceptionBookings.length
        await api.akademie.createLeadershipCoachingException({
          date: this.exceptionForm.date,
          mode: this.exceptionForm.mode,
          windows:
            this.exceptionForm.mode === "custom"
              ? [{ start: this.exceptionForm.start, end: this.exceptionForm.end, slotMinutes: Number(this.exceptionForm.slotMinutes) }]
              : undefined,
          reason: this.exceptionForm.reason.trim() || undefined,
        })
        this.exceptionDialog = false
        this.editingExceptionId = ""
        const bookingNote = affectedBookings ? " Bestehende Buchungen bleiben erhalten und müssen separat geprüft werden." : ""
        this.notify("success", `${wasEditing ? "Der Sondertag wurde aktualisiert." : "Der Sondertag wurde gespeichert."}${bookingNote}`)
        await this.loadExceptions()
      } catch (error) {
        this.exceptionError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Der Sondertag konnte nicht gespeichert werden."
      } finally {
        this.savingException = false
      }
    },
    async deleteException(item) {
      this.deletingException = item._id
      try {
        await api.akademie.deleteLeadershipCoachingException(item._id)
        this.exceptions = this.exceptions.filter((entry) => entry._id !== item._id)
        this.notify("success", "Der Sondertag wurde gelöscht.")
      } catch (error) {
        this.notify("error", "Der Sondertag konnte nicht gelöscht werden.")
      } finally {
        this.deletingException = ""
      }
    },
    openRequest(request) {
      if (this.isClosedRequest(request) || !this.scheduleLoaded) return
      const options = request.preferredOptions || []
      const bookedStart = request.startsAt || (request.booking && request.booking.startsAt)
      const bookedDuration =
        request.durationMinutes ||
        (request.booking && request.booking.durationMinutes) ||
        (request.booking &&
          request.booking.startsAt &&
          request.booking.endsAt &&
          Math.round((new Date(request.booking.endsAt) - new Date(request.booking.startsAt)) / 60000))
      const bookedIndex = options.findIndex(
        (option) =>
          bookedStart && new Date(option.startsAt).getTime() === new Date(bookedStart).getTime() && Number(option.durationMinutes) === Number(bookedDuration),
      )
      this.selectedRequest = request
      this.requestForm = {
        status: request.status && request.status !== "submitted" ? request.status : "confirmed",
        optionIndex: bookedIndex >= 0 ? bookedIndex : 0,
        adminNote: request.adminNote || "",
      }
      this.requestError = ""
      this.requestDialog = true
      this.$nextTick(() => this.$refs.requestForm && this.$refs.requestForm.resetValidation())
    },
    async saveRequest() {
      if (!this.$refs.requestForm.validate()) return
      if (this.requestForm.status !== "confirmed" && !this.requestForm.adminNote.trim()) {
        this.requestError = "Bitte erfasse für eine Rückfrage oder Ablehnung eine Nachricht."
        return
      }
      const selectedOption =
        this.requestForm.status === "confirmed" &&
        this.selectedRequest.preferredOptions &&
        this.selectedRequest.preferredOptions[this.requestForm.optionIndex]
      if (this.requestForm.status === "confirmed" && !selectedOption) {
        this.requestError = "Bitte wähle einen der beiden vorgeschlagenen Termine."
        return
      }
      this.savingRequest = true
      this.requestError = ""
      try {
        const data = { status: this.requestForm.status, adminNote: this.requestForm.adminNote.trim() || undefined }
        if (this.requestForm.status === "confirmed") {
          data.startsAt = new Date(selectedOption.startsAt).toISOString()
          data.durationMinutes = selectedOption.durationMinutes
        }
        await api.akademie.updateLeadershipExtraRequest(this.selectedRequest._id, data)
        this.requestDialog = false
        this.notify("success", "Die Antwort auf die Extra-Anfrage wurde gespeichert.")
        await this.loadRequests()
      } catch (error) {
        const data = error.response && error.response.data
        if (data && data.booking) {
          this.requestDialog = false
          this.selectedRequest = null
          this.notify(
            "warning",
            "Der Termin ist reserviert, aber der Teams-Link konnte noch nicht erstellt werden. Bitte erstelle ihn unter Buchungen erneut.",
          )
          await Promise.all([this.loadBookings(), this.loadRequests()])
          this.selectTab(2)
        } else {
          this.requestError = (data && (data.error || data.message)) || "Die Antwort konnte nicht gespeichert werden."
        }
      } finally {
        this.savingRequest = false
      }
    },
    formatWindows(windows) {
      if (!windows || !windows.length) return "Abweichende Buchungszeiten"
      return windows.map((item) => `${item.start} - ${item.end} (${normalizeSlotMinutes(item.slotMinutes, 30)} Min.)`).join(", ")
    },
    formatDateOnly(date) {
      return date
        ? new Date(`${String(date).slice(0, 10)}T12:00:00`).toLocaleDateString("de-CH", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "-"
    },
    formatDateTime(date) {
      return date
        ? new Date(date).toLocaleString("de-CH", {
            weekday: "short",
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-"
    },
    personName(item) {
      const user = item.user || item.userId || {}
      if (typeof user === "string") return item.name || "Führungskraft"
      return [user.vorname || user.firstName, user.nachname || user.lastName].filter(Boolean).join(" ") || user.name || item.name || "Führungskraft"
    },
    personMeta(item) {
      const user = item.user || item.userId || {}
      const studio = item.center || user.studio_location || user.studio || ""
      const center = typeof studio === "string" ? studio : studio.name || studio.studioName || ""
      const role = item.function || user.function || user.funktion || user.position || ""
      return [center, role].filter(Boolean).join(" · ") || "Leadership"
    },
    requestStatus(status) {
      const labels = { submitted: "Offen", confirmed: "Bestätigt", declined: "Abgelehnt", needs_info: "Rückfrage", cancelled: "Storniert" }
      return labels[status || "submitted"] || "Offen"
    },
    bookingStatus(status) {
      const labels = {
        approval_pending: "Bestätigt · Teams folgt",
        confirmed: "Bestätigt",
        meeting_pending: "Bestätigt · Teams folgt",
        meeting_failed: "Teams-Link fehlgeschlagen",
        declined: "Abgelehnt",
        cancelled: "Storniert",
        canceled: "Storniert",
      }
      return labels[status || "confirmed"] || status || "Bestätigt"
    },
    adminBookingIcon(status) {
      const icons = {
        approval_pending: "mdi-clock-outline",
        confirmed: "mdi-check-circle-outline",
        meeting_pending: "mdi-link-variant-plus",
        meeting_failed: "mdi-link-variant-off",
        declined: "mdi-close-circle-outline",
        cancelled: "mdi-calendar-remove-outline",
        canceled: "mdi-calendar-remove-outline",
      }
      return icons[status || "confirmed"] || "mdi-account-clock-outline"
    },
    isActiveBooking(booking) {
      return !["declined", "cancelled", "canceled"].includes(booking.status)
    },
    canCancelAdminBooking(booking) {
      return this.isActiveBooking(booking)
    },
    canRetryMeeting(booking) {
      return Boolean(
        this.isActiveBooking(booking) &&
          !(booking.joinUrl || booking.teamsJoinUrl) &&
          ["approval_pending", "meeting_pending", "meeting_failed"].includes(booking.status),
      )
    },
    isClosedRequest(request) {
      return ["confirmed", "declined", "cancelled"].includes(request.status)
    },
    exceptionBookingCount(date) {
      return this.bookings.filter((booking) => this.isActiveBooking(booking) && zurichDateKey(booking.startsAt) === date).length
    },
  },
}
</script>

<style scoped>
.coaching-admin {
  color: #f4f4f5;
  font-size: 1rem;
}

.manager-header,
.panel-heading,
.request-top,
.dialog-title {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.manager-header,
.panel-heading {
  justify-content: space-between;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.manager-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
}

.manager-header p,
.panel-heading p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.refresh-action,
.edit-action,
.delete-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem !important;
  color: #fff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.delete-action {
  color: #ff8d98 !important;
}

.edit-action {
  color: #93c5fd !important;
}

.manager-nav {
  display: flex;
  gap: 0.375rem;
  margin: 1rem 0;
  padding: 0.375rem;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.manager-nav button {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.5rem 0.75rem;
  border: 0;
  border-radius: 0.6875rem;
  color: #a3a3a8;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 750;
  white-space: nowrap;
}

.manager-nav button.active {
  color: #fff;
  background: rgba(0, 150, 64, 0.16);
}

.manager-nav .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.manager-nav small {
  display: grid;
  min-width: 1.375rem;
  height: 1.375rem;
  place-items: center;
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.08);
  font-size: 0.75rem;
}

.tab-content {
  overflow: visible;
  background: transparent !important;
}

.panel {
  min-height: 22rem;
}

.panel-heading {
  align-items: center;
  margin-bottom: 1rem;
}

.panel-heading h3 {
  margin: 0 0 0.1875rem;
  color: #fff;
  font-size: 1.125rem;
}

.compact-action,
.primary-action,
.request-action {
  min-height: 2.5rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.window-list,
.item-list {
  display: grid;
  gap: 0.625rem;
}

.window-row {
  display: grid;
  grid-template-columns: minmax(28rem, 1.35fr) minmax(25rem, 1fr) 2.75rem;
  align-items: end;
  gap: 0.625rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.025);
}

.window-main-fields {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(9rem, 1.35fr) repeat(3, minmax(7rem, 1fr));
  gap: 0.625rem;
}

.window-validity {
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: end;
  gap: 0.625rem;
  padding-left: 0.75rem;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.window-validity-heading {
  display: flex;
  min-height: 2.5rem;
  align-items: center;
  gap: 0.5rem;
}

.window-validity-heading > .v-icon {
  color: #7ee5a6;
  font-size: 1.125rem;
}

.window-validity-heading strong,
.window-validity-heading small {
  display: block;
  white-space: nowrap;
}

.window-validity-heading strong {
  color: #f4f4f5;
  font-size: 0.75rem;
}

.window-validity-heading small {
  margin-top: 0.125rem;
  color: #a3a3a8;
  font-size: 0.75rem;
}

.window-validity-fields {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(9rem, 1fr));
  gap: 0.625rem;
}

.empty-inline {
  padding: 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.booking-rules {
  margin-top: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgba(196, 181, 253, 0.2);
  border-radius: 0.875rem;
  background: rgba(139, 92, 246, 0.06);
}

.booking-rules-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
}

.booking-rules-heading > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.booking-rules-heading .v-icon {
  color: currentColor;
}

.booking-rules-heading h4 {
  margin: 0 0 0.125rem;
  color: #fff;
  font-size: 1rem;
}

.booking-rules-heading p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.rules-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
}

.rule-card {
  min-width: 0;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.12);
}

.rule-card p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.5;
}

.self-service-note {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.08);
}

.self-service-note > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.self-service-note .v-icon {
  color: currentColor;
}

.self-service-note strong {
  color: #fff;
  font-size: 0.9375rem;
}

.self-service-note p {
  margin: 0.1875rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.panel-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.list-card {
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.025);
}

.booking-card {
  position: relative;
  overflow: hidden;
  align-items: stretch;
  padding: 1rem;
  background: radial-gradient(circle at 100% 0%, rgba(0, 150, 64, 0.07), transparent 22rem), rgba(255, 255, 255, 0.025);
}

.booking-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0.25rem;
  background: #58d388;
  content: "";
}

.booking-card.is-approval_pending::before,
.booking-card.is-meeting_pending::before {
  background: #f5c451;
}

.booking-card.is-meeting_failed::before,
.booking-card.is-declined::before {
  background: #ff8d98;
}

.booking-person-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.booking-date {
  display: flex !important;
  align-items: center;
  gap: 0.3125rem;
  color: #c7c7cb !important;
  font-size: 0.875rem !important;
}

.booking-date .v-icon,
.list-copy small .v-icon {
  color: #7ee5a6;
  font-size: 1rem;
}

.booking-topic {
  margin: 0.625rem 0;
  padding: 0.625rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.6875rem;
  background: rgba(0, 0, 0, 0.12);
}

.booking-topic > span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.booking-topic p {
  margin-top: 0.1875rem !important;
  color: #f4f4f5 !important;
  font-size: 0.875rem !important;
  line-height: 1.45;
  white-space: pre-wrap;
}

.list-copy small {
  display: flex;
  align-items: flex-start;
  gap: 0.3125rem;
  font-size: 0.875rem;
  line-height: 1.45;
}

.list-copy .admin-note {
  margin-top: 0.5rem;
  color: #d4d4d8;
}

.list-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.list-icon .v-icon {
  color: currentColor;
}

.list-icon.icon-approval_pending,
.list-icon.icon-meeting_pending {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.list-icon.icon-meeting_failed,
.list-icon.icon-declined {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.list-icon.is-closed {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.list-icon.is-custom {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.list-icon.is-extra {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.list-card strong,
.request-top strong {
  color: #fff;
  font-size: 0.9375rem;
}

.list-card p,
.list-card small,
.request-top p {
  display: block;
  margin: 0.125rem 0 0;
  color: #a3a3a8;
  font-size: 0.75rem;
}

.exception-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.booking-warning {
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.3125rem;
  margin-top: 0.5rem;
  padding: 0.3125rem 0.5rem;
  border: 1px solid rgba(245, 158, 11, 0.24);
  border-radius: 0.625rem;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.09);
  font-size: 0.75rem;
  font-weight: 750;
  text-align: left;
}

.booking-warning .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.booking-actions {
  display: flex;
  max-width: 31rem;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.muted-badge,
.status-badge {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 750;
}

.request-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.request-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.request-top > div {
  min-width: 0;
  flex: 1 1 auto;
}

.status-submitted {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.status-approval_pending {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.status-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.status-meeting_failed {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.status-meeting_pending {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.status-cancelled,
.status-canceled {
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.06);
}

.status-declined {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.status-needs_info {
  color: #93c5fd;
  background: rgba(59, 130, 246, 0.12);
}

.request-copy {
  margin: 0.875rem 0;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.14);
}

.request-copy span {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
  text-transform: uppercase;
}

.request-copy p {
  margin: 0.1875rem 0 0.75rem;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.request-copy p:last-child {
  margin-bottom: 0;
}

.preferred-list {
  display: grid;
  gap: 0.375rem;
  margin-bottom: 0.875rem;
}

.preferred-list span {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #a3a3a8;
  font-size: 0.75rem;
}

.preferred-list .v-icon {
  color: #c4b5fd;
  font-size: 1rem;
}

.request-action {
  width: 100%;
  margin-top: auto;
}

.empty-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  text-align: center;
}

.empty-state > .v-icon {
  color: #7ee5a6;
  font-size: 2.75rem;
}

.empty-state h3 {
  margin: 0.75rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.dialog-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-title {
  flex-wrap: nowrap;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-title > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.dialog-title > span .v-icon {
  color: currentColor;
}

.dialog-title small {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 750;
}

.dialog-title h3 {
  margin: 0;
  color: #fff;
  font-size: 1.125rem;
}

.dialog-body {
  padding: 1.25rem !important;
}

.request-choice-section {
  margin-bottom: 1rem;
}

.request-choice-label {
  display: block;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.request-choice-group {
  margin-top: 0.5rem;
  padding-top: 0;
}

.request-choice {
  min-height: 4rem;
  margin: 0 0 0.5rem !important;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
}

.request-choice-copy {
  display: grid;
  gap: 0.1875rem;
}

.request-choice-copy strong {
  color: #fff;
  font-size: 1rem;
}

.request-choice-copy span {
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.4;
}

.request-choice-help {
  margin: -0.25rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.booking-alert {
  font-size: 0.875rem;
  line-height: 1.5;
}

.alert-action {
  min-height: 2.25rem;
  margin-top: 0.375rem;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.form-grid .full-grid-field {
  grid-column: 1 / -1;
}

.dialog-actions {
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirm-card {
  padding: 1.5rem;
  text-align: center;
}

.warning-icon {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  margin: 0 auto;
  place-items: center;
  border-radius: 0.8125rem;
  color: #ff8d98;
  background: rgba(244, 63, 94, 0.12);
}

.warning-icon .v-icon {
  color: currentColor;
}

.confirm-card h3 {
  margin: 0.875rem 0 0.25rem;
  color: #fff;
  font-size: 1.25rem;
}

.confirm-card p,
.confirm-card small {
  display: block;
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.confirm-card small {
  margin-top: 0.75rem;
}

.confirm-card > div {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

@media (max-width: 68rem) {
  .window-row {
    grid-template-columns: minmax(0, 1fr) 2.75rem;
    align-items: start;
  }

  .window-main-fields {
    grid-column: 1;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .window-validity {
    grid-column: 1 / -1;
    grid-row: 2;
    padding-top: 0.75rem;
    padding-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-left: 0;
  }

  .rules-grid {
    grid-template-columns: 1fr;
  }

  .window-row .delete-action {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  .request-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 47.99rem) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .window-main-fields,
  .window-validity,
  .window-validity-fields {
    grid-template-columns: 1fr;
  }

  .self-service-note {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .self-service-note .compact-action {
    grid-column: 2;
    justify-self: start;
  }

  .booking-card {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .booking-card .booking-actions {
    grid-column: 1 / -1;
    max-width: none;
    justify-self: stretch;
    justify-content: flex-start;
  }

  .booking-card .booking-actions .compact-action {
    flex: 1 1 12rem;
  }
}

@media (max-width: 37.49rem) {
  .manager-header p {
    display: none;
  }

  .panel-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .exception-card {
    grid-template-columns: 2.75rem minmax(0, 1fr);
  }

  .exception-card .exception-actions {
    grid-column: 2;
    justify-self: end;
  }

  .dialog-actions {
    flex-wrap: wrap;
  }

  .dialog-actions .v-btn {
    flex: 1 1 auto;
  }
}
</style>
